import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  EnvelopeIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { demoSignupsAPI } from '../services/api';
import PublicSocialLinks from '../components/PublicSocialLinks';
import PageMeta from '../components/PageMeta';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { blogPosts, formatBlogDate } from '../content/blogPosts';

const mockViews = [
  { id: 'capture', label: 'Capture' },
  { id: 'review', label: 'Review' },
  { id: 'plan', label: 'Plan' },
];

const mockReceipts = [
  {
    id: 'costco-run',
    merchant: 'Costco',
    date: 'Today, 8:42 AM',
    total: '$86.14',
    category: 'Groceries',
    followUp: 'Review extracted items',
    status: 'Ready to save',
    items: [
      { name: 'Organic Chicken Breast', amount: '$22.99' },
      { name: 'Greek Yogurt 24-pack', amount: '$11.49' },
      { name: 'Blueberries', amount: '$6.89' },
    ],
  },
  {
    id: 'target-home',
    merchant: 'Target',
    date: 'Yesterday, 6:15 PM',
    total: '$48.27',
    category: 'Home',
    followUp: 'Confirm category mapping',
    status: 'Needs review',
    items: [
      { name: 'Paper Towels', amount: '$14.99' },
      { name: 'Dish Soap', amount: '$5.69' },
      { name: 'Storage Bins', amount: '$19.99' },
    ],
  },
  {
    id: 'jetro-bulk',
    merchant: 'Jetro',
    date: 'Monday, 10:03 AM',
    total: '$122.08',
    category: 'Business',
    followUp: 'Add to shopping list',
    status: 'Saved to ledger',
    items: [
      { name: 'Produce Crate', amount: '$35.50' },
      { name: 'Disposable Cups', amount: '$18.20' },
      { name: 'Sparkling Water Case', amount: '$24.99' },
    ],
  },
];

const categoryStats = [
  { label: 'Groceries budget', amount: '$640', accent: 'bg-emerald-400' },
  { label: 'Recurring due soon', amount: '3', accent: 'bg-amber-400' },
  { label: 'Shopping list items', amount: '18', accent: 'bg-sky-400' },
];

const faqItems = [
  {
    question: 'What is Zaphar?',
    answer:
      'Zaphar is an AI-assisted expense tracker and budgeting app. Capture purchases from receipts, manual entries, historical imports, or connected accounts, then organize them alongside budgets, recurring bills, goals, shopping lists, and spending insights.',
  },
  {
    question: 'How does AI receipt capture work?',
    answer:
      'Take a photo or upload a receipt and Zaphar creates a draft with the merchant, date, total, and line items. You review the extracted details, correct anything that needs a fix, confirm categories, and then save the cleaned purchase to your ledger.',
  },
  {
    question: 'Can I use Zaphar without a receipt?',
    answer:
      'Yes. You can enter a transaction manually or import historical transactions. Voice Entry is also available on supported web and Android builds for guided hands-free entry.',
  },
  {
    question: 'Can I connect a bank or credit card?',
    answer:
      'Yes. Pro can automatically import eligible checking and credit-card transactions with read-only access. You choose which accounts to include, and Zaphar cannot move money or make changes at your bank. Online banking credentials are handled by the connection provider and are not seen or stored by Zaphar.',
  },
  {
    question: 'What does Zaphar cost?',
    answer:
      'Basic includes unlimited transaction tracking, budgets, recurring bills, goals, and lists, with monthly limits on receipt scans, Spending AI questions, voice entries, and sharing. Pro currently costs $3.99 per month or $39.99 per year, and new accounts receive 30 days of Pro access. Pricing may change before public launch.',
  },
  {
    question: 'What is Spending AI?',
    answer:
      'Spending AI answers questions about the spending recorded in your active account, including totals, categories, merchants, and month-over-month trends. It is a way to understand your records, not a financial advisor or a substitute for your own decisions.',
  },
  {
    question: 'Can I share an account with someone else?',
    answer:
      'Yes. You can invite people to collaborate on an account, choose full-access or read-only roles, and manage feature access for members. Shared-account limits depend on whether the account is on Basic or Pro.',
  },
  {
    question: 'How does Zaphar handle recurring expenses?',
    answer:
      'Track recurring income and expenses on weekly, bi-weekly, monthly, quarterly, semiannual, or annual schedules. Zaphar shows upcoming items and lets you record when a scheduled payment is actually received or paid.',
  },
  {
    question: 'Is my financial data shared with other people?',
    answer:
      'Your financial data stays within the account you use unless you intentionally invite someone to share it. We use the information you save to provide Zaphar features and handle connected-account data with read-only access. See the Privacy Policy for more details.',
  },
  {
    question: 'What devices does Zaphar support?',
    answer:
      'Zaphar supports web browsers and Android. The current rollout is focused on early access, and an iOS version is not currently available. Join the early access list for platform updates.',
  },
  {
    question: 'When can I start using Zaphar?',
    answer:
      'Zaphar is rolling out to early access users first. Add your email above and we will send the first walkthrough and availability updates when they are ready.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

function DemoPage() {
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState({ status: 'idle', message: '' });
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [activeView, setActiveView] = useState('capture');
  const [selectedReceiptId, setSelectedReceiptId] = useState(mockReceipts[0].id);
  const [scrollY, setScrollY] = useState(0);
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  const selectedReceipt = mockReceipts.find((receipt) => receipt.id === selectedReceiptId) || mockReceipts[0];

  useEffect(() => {
    let ticking = false;

    const updateScrollPosition = () => {
      setScrollY(window.scrollY || 0);
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(updateScrollPosition);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 640);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = String(email || '').trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);

    if (!isValidEmail) {
      setEmailState({
        status: 'error',
        message: 'Enter a valid email to join the early access list.',
      });
      return;
    }

    try {
      setIsSubmittingEmail(true);
      const response = await demoSignupsAPI.create({
        email: normalizedEmail,
        source: 'demo-page',
        referrer: typeof window !== 'undefined' ? window.location.href : '',
      });

      setEmailState({
        status: 'success',
        message: response?.alreadySubscribed
          ? 'That email is already on the list. We will reach out when the demo opens.'
          : "You're on the list. We'll send the demo drop as soon as it opens up.",
      });
      setEmail(normalizedEmail);
    } catch (error) {
      const fallbackMessage = error?.response
        ? 'Could not save your email right now. Please try again.'
        : 'Could not reach the signup service right now. Please try again in a minute.';

      setEmailState({
        status: 'error',
        message: error?.response?.data?.error || fallbackMessage,
      });
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const summaryHeading =
    activeView === 'capture'
      ? 'Receipt capture flow'
      : activeView === 'review'
        ? 'Receipt review and edits'
        : 'Planning tools in one place';

  const summaryCopy =
    activeView === 'capture'
      ? 'Take a photo or upload a receipt, then move straight into the extraction and cleanup flow the app already supports.'
      : activeView === 'review'
        ? 'Inspect extracted line items, adjust categories, and save cleaned transactions into the ledger with less friction.'
        : 'Use budgets, recurring transaction tracking, and shopping lists to stay organized after the receipt is saved.';

  const clampShift = (value, min, max) => Math.min(Math.max(value, min), max);
  const buildScrollStyle = (distance, maxUp = 0, maxDown = 0) => ({
    transform: `translate3d(0, ${clampShift(distance, maxUp, maxDown)}px, 0)`,
  });
  const buildShuffleStyle = ({
    distance = 0,
    maxUp = -12,
    maxDown = 12,
    scale = 1,
  }) => {
    if (!isMobileView) {
      return buildScrollStyle(distance, maxUp, maxDown);
    }

    const translateY = clampShift(distance, maxUp, maxDown);
    const scaled = Math.max(0.988, Math.min(1.01, scale - Math.abs(translateY) * 0.00035));

    return {
      transform: `translate3d(0, ${translateY}px, 0) scale(${scaled})`,
    };
  };

  const signupForm = (className = '', style = undefined) => (
    <form
      className={`demo-scroll-layer mt-6 max-w-xl rounded-[24px] border border-white/12 bg-white/8 p-4 backdrop-blur-xl sm:mt-8 sm:rounded-[28px] ${className}`.trim()}
      onSubmit={handleEmailSubmit}
      style={style}
    >
      <label htmlFor="demo-email" className="block text-sm font-medium text-stone-100">
        Join the early access list
      </label>
      <p className="mt-1 text-sm text-stone-400">Drop your email and we&apos;ll send the first walkthrough when it&apos;s ready.</p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <EnvelopeIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-500" aria-hidden="true" />
          <input
            id="demo-email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (emailState.status !== 'idle') {
                setEmailState({ status: 'idle', message: '' });
              }
            }}
            placeholder="name@email.com"
            className="w-full rounded-2xl border border-white/10 bg-stone-950/75 py-3 pl-12 pr-4 text-sm text-stone-50 outline-none transition placeholder:text-stone-500 focus:border-amber-300/60 focus:ring-2 focus:ring-amber-200/20"
          />
        </div>
        <button
          type="submit"
          disabled={isSubmittingEmail}
          className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-amber-300 px-5 py-3 text-sm font-semibold text-stone-950 transition hover:bg-amber-200"
        >
          {isSubmittingEmail ? 'Saving...' : 'Notify me'}
        </button>
      </div>

      {emailState.message ? (
        <div
          className={`mt-4 flex items-start gap-2 rounded-2xl px-4 py-3 text-sm ${
            emailState.status === 'success'
              ? 'border border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
              : 'border border-rose-400/30 bg-rose-400/10 text-rose-100'
          }`}
        >
          <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{emailState.message}</span>
        </div>
      ) : null}
    </form>
  );

  return (
    <div className="demo-shell min-h-screen overflow-hidden bg-stone-950 text-stone-100">
      <PageMeta
        title="Zaphar | AI Budgeting & Receipt Tracker"
        description="Zaphar turns receipts and everyday spending into clearer budgets, recurring expense plans, shopping lists, and practical money insights."
      />
      <div className="demo-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="demo-glow demo-glow-one" aria-hidden="true" />
      <div className="demo-glow demo-glow-two" aria-hidden="true" />

      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-4 pb-12 pt-4 sm:px-8 sm:pb-16 sm:pt-6 lg:px-10">
        <header className="flex flex-col items-start gap-4">
          <div className="max-w-xl">
            <p className="demo-kicker text-xs font-semibold uppercase tracking-[0.32em] text-amber-300/85">Zaphar</p>
            <h1 className="demo-subhead mt-2 max-w-lg text-xl font-semibold tracking-tight text-stone-50 sm:text-3xl">
              AI budgeting app that scans your receipts and plans your spending.
            </h1>
          </div>
        </header>

        <main className="mt-8 flex flex-col gap-8 lg:mt-10 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-start lg:gap-10">
          <section
            className="demo-scroll-layer flex max-w-2xl flex-col"
            style={buildScrollStyle(scrollY * -0.055, -34, 0)}
          >
            <div className="inline-flex w-fit self-start items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-amber-200">
              <ReceiptPercentIcon className="h-4 w-4" aria-hidden="true" />
              Coming Soon
            </div>

            <h2 className="mt-5 space-y-2.5 text-[2.2rem] font-semibold leading-[0.9] tracking-tight text-stone-50 sm:mt-6 sm:space-y-2 sm:text-5xl">
              <span className="demo-hero-line block">Smarter tracking</span>
              <span className="demo-hero-line demo-hero-line-delay-1 block">Clearer spending</span>
              <span className="demo-hero-line demo-hero-line-delay-2 block">Financial freedom</span>
            </h2>

            <p className="demo-body-copy mt-5 max-w-xl text-sm leading-6 text-stone-300 sm:mt-6 sm:text-lg sm:leading-7">
              Scan receipts, organize expenses, plan monthly budgets, and stay
              focused on your financial goals.
            </p>

            {signupForm('hidden lg:block', buildScrollStyle(scrollY * -0.018, -10, 0))}

          </section>

          <section
            className="demo-panel demo-scroll-layer demo-mobile-shuffle-card relative order-2 rounded-[28px] border border-white/12 bg-[#181412]/85 p-4 shadow-2xl shadow-black/25 backdrop-blur-xl sm:rounded-[36px] sm:p-6 lg:order-none"
            style={buildShuffleStyle({ distance: scrollY * 0.022, maxUp: -2, maxDown: 12, scale: 1 })}
          >
            <div className="flex flex-col gap-3 border-b border-white/8 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.26em] text-stone-500">Interactive Mockup</p>
                <h3 className="mt-2 text-xl font-semibold text-stone-50 sm:text-2xl">Zaphar Preview</h3>
              </div>
              <div className="inline-flex self-start rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-200">
                Live concept
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
              {mockViews.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => setActiveView(view.id)}
                  className={`rounded-full px-3 py-2 text-center text-sm font-medium transition sm:px-4 ${
                    activeView === view.id
                      ? 'bg-stone-100 text-stone-950'
                      : 'border border-white/10 bg-white/5 text-stone-300 hover:bg-white/10'
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-4 xl:grid-cols-[0.95fr_1.05fr] xl:gap-5">
              <div className="space-y-4">
                <div
                  className="demo-scroll-layer demo-mobile-shuffle-card rounded-[20px] border border-white/8 bg-stone-900/70 p-3.5 sm:rounded-[28px] sm:p-4"
                  style={buildShuffleStyle({ distance: scrollY * 0.01, maxUp: -1, maxDown: 6, scale: 1.002 })}
                >
                  <p className="text-sm font-medium text-stone-50">{summaryHeading}</p>
                  <p className="mt-2 text-sm leading-5 text-stone-400 sm:leading-6">{summaryCopy}</p>
                </div>

                <div
                  className="demo-scroll-layer demo-mobile-shuffle-card rounded-[20px] border border-white/8 bg-stone-900/70 p-3.5 sm:rounded-[28px] sm:p-4"
                  style={buildShuffleStyle({ distance: scrollY * 0.012, maxUp: -1, maxDown: 7, scale: 1 })}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-stone-50">Recent receipts</p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-stone-500">tap to inspect</p>
                  </div>
                  <div className="mt-3 space-y-3">
                    {mockReceipts.map((receipt) => (
                      <button
                        key={receipt.id}
                        type="button"
                        onClick={() => setSelectedReceiptId(receipt.id)}
                        className={`w-full rounded-[18px] border p-3.5 text-left transition sm:rounded-[22px] sm:p-4 ${
                          selectedReceiptId === receipt.id
                            ? 'border-amber-300/40 bg-amber-200/10'
                            : 'border-white/6 bg-white/[0.03] hover:bg-white/[0.06]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-stone-50">{receipt.merchant}</p>
                            <p className="mt-1 text-[11px] text-stone-500 sm:text-xs">{receipt.date}</p>
                          </div>
                          <p className="shrink-0 text-sm font-semibold text-stone-100">{receipt.total}</p>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full bg-white/8 px-2.5 py-1 text-xs text-stone-300">{receipt.category}</span>
                          <span className="rounded-full bg-emerald-300/10 px-2.5 py-1 text-xs text-emerald-200">{receipt.status}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div
                  className="demo-scroll-layer demo-mobile-shuffle-card rounded-[20px] border border-white/8 bg-gradient-to-br from-stone-100 to-stone-200 p-3.5 text-stone-900 sm:rounded-[28px] sm:p-5"
                  style={buildShuffleStyle({ distance: scrollY * -0.012, maxUp: -7, maxDown: 0, scale: 1.003 })}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Transaction detail</p>
                      <h4 className="mt-2 text-xl font-semibold sm:text-2xl">{selectedReceipt.merchant}</h4>
                      <p className="mt-1 text-sm text-stone-500">{selectedReceipt.date}</p>
                    </div>
                    <div className="rounded-2xl bg-stone-900 px-3 py-2 text-left text-stone-50 shadow-lg shadow-stone-950/20 sm:text-right">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400">Total</p>
                      <p className="mt-1 text-lg font-semibold">{selectedReceipt.total}</p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2.5 sm:mt-5 sm:space-y-3">
                    {selectedReceipt.items.map((item, index) => (
                      <div
                        key={`${selectedReceipt.id}-${item.name}`}
                        className="demo-ticket-row flex items-start justify-between gap-3 rounded-2xl border border-stone-300/70 bg-white/80 px-3.5 py-2.5 sm:px-4 sm:py-3"
                        style={{ animationDelay: `${index * 90}ms` }}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-900">{item.name}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-stone-500">{selectedReceipt.category}</p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-stone-900">{item.amount}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid gap-2.5 sm:mt-5 sm:gap-3">
                    <div className="rounded-2xl bg-stone-900 px-4 py-3 text-stone-50">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-stone-400">Suggested next step</p>
                      <p className="mt-2 text-base font-semibold sm:text-xl">{selectedReceipt.followUp}</p>
                    </div>
                    <div className="rounded-2xl border border-stone-300 bg-white/75 px-4 py-3">
                      <p className="text-[11px] uppercase tracking-[0.22em] text-stone-500">Current app flow</p>
                      <p className="mt-2 text-sm font-medium leading-5 text-stone-900">Review items, adjust categories, then save.</p>
                    </div>
                  </div>
                </div>

                <div
                  className="demo-scroll-layer demo-mobile-shuffle-card rounded-[20px] border border-white/8 bg-stone-900/70 p-3.5 sm:rounded-[28px] sm:p-4"
                  style={buildShuffleStyle({ distance: scrollY * 0.01, maxUp: -1, maxDown: 5, scale: 1 })}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-stone-50">Budget snapshot</p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-stone-500">demo data</p>
                  </div>
                  <div className="mt-4 space-y-3">
                    {categoryStats.map((stat) => (
                      <div key={stat.label}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-300">{stat.label}</span>
                          <span className="font-medium text-stone-50">{stat.amount}</span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/8">
                          <div className={`h-full rounded-full ${stat.accent}`} style={{ width: stat.amount === '$640' ? '72%' : stat.amount === '18' ? '64%' : '36%' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {signupForm('order-3 lg:hidden')}
        </main>

        <section
          aria-labelledby="demo-features-heading"
          className="relative mt-14 border-t border-white/8 pt-10 sm:mt-16 sm:pt-12"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/85">
              Personal finance, organized
            </p>
            <h2
              id="demo-features-heading"
              className="mt-3 text-2xl font-semibold tracking-tight text-stone-50 sm:text-3xl"
            >
              Budget and expense tracking built around real purchases
            </h2>
            <p className="mt-4 text-sm leading-6 text-stone-300 sm:text-base sm:leading-7">
              Zaphar connects receipt details with practical planning tools, so
              everyday spending becomes easier to review and use.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">AI receipt scanner</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Upload or photograph a receipt, review extracted line items,
                and clean up the details before saving.
              </p>
            </article>
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">Personal expense tracker</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Organize purchases by category and keep searchable transaction
                details together instead of losing context.
              </p>
            </article>
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">Monthly budget planner</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Compare planned category budgets with actual spending to see
                where your money is going.
              </p>
            </article>
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">Recurring expense tracking</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Keep repeating transactions visible alongside upcoming
                spending and the rest of your financial activity.
              </p>
            </article>
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">Shopping list organization</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Turn purchase history into useful shopping lists and keep
                planned items connected to past spending.
              </p>
            </article>
            <article className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-base font-semibold text-stone-50">Spending insights</h3>
              <p className="mt-2 text-sm leading-6 text-stone-400">
                Review categories, budgets, and recent activity from a clearer
                personal finance dashboard.
              </p>
            </article>
          </div>
        </section>

        <section
          aria-labelledby="demo-blog-heading"
          className="relative mt-14 border-t border-white/8 pt-10 sm:mt-16 sm:pt-12"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/85">
                From the journal
              </p>
              <h2 id="demo-blog-heading" className="mt-3 text-2xl font-semibold tracking-tight text-stone-50 sm:text-3xl">
                Better money habits, one useful idea at a time
              </h2>
            </div>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-amber-200 transition hover:text-amber-100">
              View all posts
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {blogPosts.slice(0, 3).map((post) => (
              <article key={post.slug} className="group flex flex-col rounded-[24px] border border-white/10 bg-white/[0.04] p-5 transition hover:-translate-y-1 hover:border-amber-300/25">
                <div className="flex flex-wrap gap-2 text-xs text-stone-500">
                  <span className="text-amber-200">{post.category}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={post.publishedAt}>{formatBlogDate(post.publishedAt)}</time>
                </div>
                <h3 className="mt-4 text-lg font-semibold leading-6 text-stone-50">{post.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-6 text-stone-400">{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-stone-200">
                  Read article
                  <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section
          aria-labelledby="demo-faq-heading"
          className="relative mt-14 border-t border-white/8 pt-10 sm:mt-16 sm:pt-12"
        >
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/85">
              Questions
            </p>
            <h2 id="demo-faq-heading" className="mt-3 text-2xl font-semibold tracking-tight text-stone-50 sm:text-3xl">
              Frequently asked questions
            </h2>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {faqItems.map((item) => (
              <details
                key={item.question}
                className="group rounded-[20px] border border-white/10 bg-white/[0.04] p-5 open:bg-white/[0.06]"
              >
                <summary className="cursor-pointer list-none text-base font-semibold text-stone-50 marker:content-none">
                  <span className="flex items-center justify-between gap-3">
                    {item.question}
                    <span className="text-lg text-stone-500 transition group-open:rotate-45">+</span>
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-6 text-stone-400">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="mt-10 flex flex-col items-center gap-4 border-t border-white/8 pt-6 sm:mt-12 sm:pt-8">
          <PublicSocialLinks />
          <div className="flex items-center gap-4 text-xs text-stone-500">
            <span>&copy; {new Date().getFullYear()} Pevail LLC</span>
            <Link to="/privacy" className="transition hover:text-stone-300">Privacy Policy</Link>
          </div>
        </footer>
      </div>

      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </div>
  );
}

export default DemoPage;
